/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { forwardRef } from 'react';
import type { ButtonProps } from '@atlaskit/button/new';
import { cssMap, jsx } from '@atlaskit/css';
import FilterIcon from '@atlaskit/icon/core/filter';
// eslint-disable-next-line jira/import/no-direct-popup
import type { TriggerProps } from '@atlaskit/popup';
import { Hide, Inline, Pressable } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import StatusWarningIcon from '@atlaskit/icon/core/status-warning';
import { useIntl } from '@atlassian/jira-intl';
import { mergeRefs } from '@atlassian/jira-merge-refs/src/index.tsx';
import messages from '../messages.tsx';

type Props = {
	onClick: ButtonProps['onClick'];
	triggerProps: TriggerProps;
	formattedTooltipContent: JSX.Element;
};

const styles = cssMap({
	contentContainer: {
		height: '20px',
	},
	errorState: {
		display: 'inline-flex',
		boxSizing: 'border-box',
		width: 'auto',
		maxWidth: '100%',
		position: 'relative',
		alignItems: 'baseline',
		justifyContent: 'center',
		columnGap: token('space.050'),
		borderRadius: token('radius.small'),
		borderWidth: 0,
		flexShrink: 0,
		height: '2rem',
		font: token('font.body'),
		fontWeight: token('font.weight.medium'),
		paddingBlock: token('space.075'),
		paddingInlineEnd: token('space.150'),
		paddingInlineStart: token('space.150'),
		textAlign: 'center',
		transition: 'background 0.1s ease-out',
		verticalAlign: 'middle',
		'&::after': {
			borderRadius: 'inherit',
			inset: token('space.0'),
			borderStyle: 'solid',
			borderWidth: token('border.width'),
			pointerEvents: 'none',
			position: 'absolute',
			content: '""',
			borderColor: token('color.border.accent.red'),
		},
		backgroundColor: token('color.background.accent.red.subtler'),
		color: token('color.text.accent.red'),
		'&:visited': {
			color: token('color.text.accent.red'),
		},
		'&:hover': {
			color: token('color.text.accent.red'),
		},
		'&:focus': {
			color: token('color.text.accent.red'),
		},
	},
});

export const FilterPopupErrorTrigger = forwardRef<HTMLButtonElement, Props>(
	({ onClick, triggerProps, formattedTooltipContent }, ref) => {
		const { formatMessage } = useIntl();

		return (
			<Tooltip content={formattedTooltipContent} hideTooltipOnMouseDown>
				{(tooltipProps) => (
					<Pressable
						xcss={styles.errorState}
						{...tooltipProps}
						{...triggerProps}
						aria-label={formatMessage(messages.filterButton)}
						interactionName="filter-popup-load-content"
						onClick={onClick}
						ref={mergeRefs(ref, triggerProps.ref)}
					>
						<Inline as="span" alignBlock="center" space="space.075" xcss={styles.contentContainer}>
							<FilterIcon label="" />
							<Hide as="span" below="md">
								{formatMessage(messages.filterButton)}
							</Hide>
							<StatusWarningIcon color={token('color.icon.danger')} label="" />
						</Inline>
					</Pressable>
				)}
			</Tooltip>
		);
	},
);
